/**
 * DomainsTable - Tabela de domínios com paginação
 */
import React from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Paper,
    Checkbox,
    Tooltip,
    Button,
    Typography,
    FormControl,
    Select,
    MenuItem
} from '@mui/material';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import DomainRow from '../DomainRow';

const DomainsTable = ({
    domains,
    selectedDomainsSet,
    hypestat,
    sortBy,
    sortDirection,
    onSort,
    onVisitDomain,
    onFetchData,
    onProcessDomain,
    onIgnoreDomain,
    onEnrichDomain,
    onDeleteDomain,
    currentPage,
    totalPages,
    totalDomains,
    itemsPerPage,
    setItemsPerPage,
    onPageChange
}) => {
    const theme = useMuiTheme();

    return (
        <>
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30,30,30,0.4)' : '#ffffff',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                    boxShadow: theme.palette.mode === 'dark' ? '0 4px 24px rgba(0,0,0,0.2)' : '0 4px 24px rgba(0,0,0,0.02)',
                    '&::-webkit-scrollbar': { width: '8px', height: '8px' },
                    '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: '4px' }
                }}
            >
                <Table sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid', borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 600, bgcolor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#fcfcfc' } }}>
                            <TableCell padding="checkbox">
                                <Tooltip title={hypestat.selectedDomains.length === domains.length ? "Desmarcar todos" : "Selecionar todos para scraping"}>
                                    <Checkbox
                                        indeterminate={hypestat.selectedDomains.length > 0 && hypestat.selectedDomains.length < domains.length}
                                        checked={domains.length > 0 && hypestat.selectedDomains.length === domains.length}
                                        onChange={hypestat.toggleSelectAll.bind(null, domains)}
                                    />
                                </Tooltip>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'domain' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'domain'}
                                    direction={sortBy === 'domain' ? sortDirection : 'asc'}
                                    onClick={() => onSort('domain')}
                                >
                                    Domínio
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'siteName' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'siteName'}
                                    direction={sortBy === 'siteName' ? sortDirection : 'asc'}
                                    onClick={() => onSort('siteName')}
                                >
                                    Nome do Site
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'articleCount' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'articleCount'}
                                    direction={sortBy === 'articleCount' ? sortDirection : 'desc'}
                                    onClick={() => onSort('articleCount')}
                                >
                                    Artigos
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'estimatedAudience' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'estimatedAudience'}
                                    direction={sortBy === 'estimatedAudience' ? sortDirection : 'desc'}
                                    onClick={() => onSort('estimatedAudience')}
                                >
                                    Audiência (Diária)
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>
                                Audiência Mensal
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'cpc' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'cpc'}
                                    direction={sortBy === 'cpc' ? sortDirection : 'desc'}
                                    onClick={() => onSort('cpc')}
                                >
                                    CPC
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'valuation' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'valuation'}
                                    direction={sortBy === 'valuation' ? sortDirection : 'desc'}
                                    onClick={() => onSort('valuation')}
                                >
                                    Valoração
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'tier' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'tier'}
                                    direction={sortBy === 'tier' ? sortDirection : 'asc'}
                                    onClick={() => onSort('tier')}
                                >
                                    Tier
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'priority' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'priority'}
                                    direction={sortBy === 'priority' ? sortDirection : 'asc'}
                                    onClick={() => onSort('priority')}
                                >
                                    Prioridade
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'nextUpdateAt' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'nextUpdateAt'}
                                    direction={sortBy === 'nextUpdateAt' ? sortDirection : 'asc'}
                                    onClick={() => onSort('nextUpdateAt')}
                                >
                                    Próxima Atualização
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'firstSeen' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'firstSeen'}
                                    direction={sortBy === 'firstSeen' ? sortDirection : 'desc'}
                                    onClick={() => onSort('firstSeen')}
                                >
                                    Primeira Vez
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'status' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'status'}
                                    direction={sortBy === 'status' ? sortDirection : 'asc'}
                                    onClick={() => onSort('status')}
                                >
                                    Status
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={sortBy === 'processedAt' ? sortDirection : false}>
                                <TableSortLabel
                                    active={sortBy === 'processedAt'}
                                    direction={sortBy === 'processedAt' ? sortDirection : 'desc'}
                                    onClick={() => onSort('processedAt')}
                                >
                                    Processado em
                                </TableSortLabel>
                            </TableCell>
                            <TableCell>Categorias</TableCell>
                            <TableCell>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {domains.map((domain) => (
                            <DomainRow
                                key={domain._id}
                                domain={domain}
                                isSelected={selectedDomainsSet.has((domain.registrableDomain || domain.domain).toLowerCase().trim())}
                                onToggleSelect={() => hypestat.toggleDomain(domain)}
                                onVisit={() => onVisitDomain(domain)}
                                onFetchData={() => onFetchData(domain)}
                                onProcess={() => onProcessDomain(domain)}
                                onIgnore={() => onIgnoreDomain(domain)}
                                onEnrich={() => onEnrichDomain(domain)}
                                onDelete={() => onDeleteDomain(domain)}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Controles de Paginação */}
            {domains.length > 0 && (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mt={2}
                    p={2}
                    sx={{
                        backgroundColor: theme.palette.background.default,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider'
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="body2" color="text.secondary">
                            Exibindo {domains.length} de {totalDomains.toLocaleString('pt-BR')} domínios
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 100 }}>
                            <Select
                                value={itemsPerPage}
                                onChange={(e) => setItemsPerPage(e.target.value)}
                                size="small"
                            >
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={50}>50</MenuItem>
                                <MenuItem value={100}>100</MenuItem>
                                <MenuItem value={200}>200</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography variant="body2" color="text.secondary">por página</Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={1}>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={currentPage <= 1}
                            onClick={() => onPageChange(1)}
                        >
                            Primeira
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={currentPage <= 1}
                            onClick={() => onPageChange(currentPage - 1)}
                        >
                            Anterior
                        </Button>
                        <Typography variant="body2" sx={{ mx: 2 }}>
                            Página {currentPage} de {totalPages}
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={currentPage >= totalPages}
                            onClick={() => onPageChange(currentPage + 1)}
                        >
                            Próxima
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={currentPage >= totalPages}
                            onClick={() => onPageChange(totalPages)}
                        >
                            Última
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};

export default DomainsTable;
